/**
 * ProjectAvatar.tsx
 *
 * Avatar de proyecto con color dinámico de la paleta del sistema.
 * Muestra imagen del proyecto si existe, iniciales con color elegido si no.
 * Forma cuadrada con border-radius suave — diferencia visual del avatar circular de usuario.
 *
 * Uso:
 *   <ProjectAvatar name="Proyecto Alpha" color="oklch(0.70 0.20 145)" />
 *   <ProjectAvatar name="Proyecto Alpha" color="oklch(0.70 0.20 145)" src="/logo.png" size="lg" />
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { cn } from '@/core/lib/utils';
import { getTextColorFromToken, getColorValue } from '@/core/lib/colors';
import { getInitials } from '@/core/lib/get-initials';

/* ── Variantes ──────────────────────────────────────────────── */

/**
 * Avatares de proyecto son cuadrados con radius-md.
 * Diferencia intencional respecto al círculo de usuario.
 */
const avatarVariants = cva('', {
  variants: {
    size: {
      xs: 'size-5 rounded-md',
      sm: 'size-6 rounded-md',
      md: 'size-8 rounded-md',
      lg: 'size-10 rounded-md',
      xl: 'size-12 rounded-md',
      '2xl': 'size-[60px] md:size-[100px] rounded-xl',
    },
  },
  defaultVariants: { size: 'md' },
});

const fallbackVariants = cva('flex items-center justify-center font-medium select-none', {
  variants: {
    size: {
      xs: 'text-[9px] rounded-md',
      sm: 'text-[10px] rounded-md',
      md: 'text-xs rounded-md',
      lg: 'text-sm rounded-md',
      xl: 'text-base rounded-md',
      '2xl': 'text-lg md:text-2xl rounded-xl',
    },
  },
  defaultVariants: { size: 'md' },
});

/* ── Props ──────────────────────────────────────────────────── */

export interface ProjectAvatarProps extends VariantProps<typeof avatarVariants> {
  /** Nombre del proyecto — usado para generar iniciales */
  name: string;
  /** Token del color de paleta (ej: "red-ember") — se resuelve internamente a oklch */
  color: string;
  /** URL de imagen o logo del proyecto */
  src?: string;
  alt?: string;
  className?: string;
}

/* ── Componente ─────────────────────────────────────────────── */

export function ProjectAvatar({ name, color, src, alt, size, className }: ProjectAvatarProps) {
  const initials = getInitials(name);
  const colorValue = getColorValue(color);
  const textColor = getTextColorFromToken(color);

  return (
    <Avatar className={cn(avatarVariants({ size }), className)}>
      <AvatarImage src={src} alt={alt ?? name} />
      <AvatarFallback
        className={cn(fallbackVariants({ size }))}
        style={{ background: colorValue, color: textColor }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
